import cx from 'classnames';
import * as React from 'react';

import Glyph from 'components/Glyph';
import Heading from 'components/Heading';
import Icon from 'components/IconDeprecated';
import Image from 'components/Image';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';

import './styles/TitleHeader.scss';

import { GlyphColor } from 'components/Glyph';
import { GlyphName } from '@remind/r101-glyphs';
import { HeadingLevelStyle } from 'components/Heading';

export type TitleHeaderSize = 'large' | 'medium' | 'small' | 'xsmall';
export type TitleHeaderImageSize = 'large' | 'medium' | 'small' | 'xsmall' | 'xxsmall' | 'xxxsmall';
export type TitleHeaderTextAlign = 'left' | 'center' | 'right';

export type Props = {
  /** Button or loading indicator */
  action?: any;
  actionPlacement?: 'right' | 'right-center' | 'bottom';
  children?: React.ReactNode;
  className?: string;
  color?: 'whiteout' | 'paper' | 'none'; // only whiteoutsupported in scss currently
  flush?: boolean;
  glyph?: GlyphName;
  glyphColor?: GlyphColor;
  icon?: React.ReactElement<typeof Icon>;
  image?: string;
  imageCircle?: boolean;
  /** Prefer imageSize—use only to restrict image `height` */
  imageClassName?: string;
  imageSize?: TitleHeaderImageSize;
  /** If used in a non-self-centering null state */
  nullState?: boolean;
  /** Makes the `title` render in serif */
  serif?: boolean;
  size?: TitleHeaderSize;
  subtext?: any;
  textAlign?: TitleHeaderTextAlign;
  // XXX we allow subtext and images without primary title
  title?: any;
  level?: HeadingLevelStyle;
};

type DefaultProps = {
  glyphColor: GlyphColor;
  size: TitleHeaderSize;
  textAlign: TitleHeaderTextAlign;
};

export default class TitleHeader extends React.Component<Props> {
  static defaultProps: DefaultProps = {
    glyphColor: 'mascot',
    size: 'medium',
    textAlign: 'center',
  };

  render() {
    const {
      action: propsAction,
      actionPlacement,
      children,
      className,
      color,
      flush,
      glyph,
      glyphColor,
      icon,
      level,
      image,
      imageCircle,
      imageClassName,
      imageSize,
      nullState,
      serif,
      size,
      subtext,
      title,
    } = this.props;
    let { textAlign } = this.props;

    const actionOnRight = actionPlacement === 'right' || actionPlacement === 'right-center';

    let levelStyle: HeadingLevelStyle | null = null;

    if (actionOnRight) {
      textAlign = 'left';
    }

    if (size === 'medium') {
      levelStyle = '2';
    } else if (size === 'large') {
      levelStyle = '1';
    } else if (size === 'small') {
      levelStyle = '4';
    }

    const contents = (
      <React.Fragment>
        {glyph && <Glyph color={glyphColor} name={glyph} solo className="TitleHeader-glyph" />}
        {icon && <Row size="small">{icon}</Row>}
        {image && (
          <Image
            src={image}
            circle={imageCircle}
            className={cx(
              'TitleHeader-img',
              imageClassName,
              imageSize && `TitleHeader-img--${imageSize}`
            )}
          />
        )}
        <Heading
          level={level || '1'}
          levelStyle={levelStyle}
          serif={serif}
          className="TitleHeader-title"
        >
          {title}
        </Heading>
        {subtext && (
          <div data-testid="titleheader-subtext" className="TitleHeader-subtext">
            {subtext}
          </div>
        )}
      </React.Fragment>
    );

    const content = children ? <div className="TitleHeader-content">{children}</div> : null;

    const action = <div className="TitleHeader-action">{propsAction}</div>;

    const body = actionOnRight ? (
      <React.Fragment>
        <MediaObject center={actionPlacement === 'right-center'} figure="right">
          <div>{contents}</div>
          {propsAction ? action : null}
        </MediaObject>
        {content}
      </React.Fragment>
    ) : (
      <React.Fragment>
        {contents}
        {content}
        {propsAction ? action : null}
      </React.Fragment>
    );

    return (
      <div
        className={cx(
          'TitleHeader',
          actionPlacement && `TitleHeader--actionPlacement--${actionPlacement}`,
          color && `TitleHeader--${color}`,
          flush && 'TitleHeader--flush',
          nullState && 'TitleHeader--null',
          size && `TitleHeader--${size}`,
          textAlign && `TitleHeader--${textAlign}`,
          className
        )}
      >
        {body}
      </div>
    );
  }
}
